import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Calendar, DoorOpen } from 'lucide-react';
import ListingAttributesFilterForm from '../ListingAttributesFilterForm';
import { createDefaultAdvancedFilters } from '../../config/searchFilterDefaults';
import { FilterSectionBoneIcon } from '../FilterSectionBoneIcon';
import { FilterDashedSeparator } from '../FilterDashedSeparator';
import { ChipPaw, filterChipBtn } from '../searchFilterChips';

const CORAL = '#FF5E4C';
const CREAM = '#FAF7F2';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

/** Slider handle icon — `public/assets/paw_white.svg` */
const SliderThumbPaw = () => (
  <img
    src="/assets/paw_white.svg"
    alt=""
    aria-hidden
    draggable={false}
    className="h-[14px] w-[14px] shrink-0 object-contain select-none"
  />
);

const FooterPawOutline = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 16 16" fill="none" className="opacity-90" aria-hidden>
    <path d="M8 4C8.55228 4 9 3.55228 9 3C9 2.44772 8.55228 2 8 2C7.44772 2 7 2.44772 7 3C7 3.55228 7.44772 4 8 4Z" stroke="white" strokeWidth="1.2" fill="none" />
    <path d="M5 5C5.55228 5 6 4.55228 6 4C6 3.44772 5.55228 3 5 3C4.44772 3 4 3.44772 4 4C4 4.55228 4.44772 5 5 5Z" stroke="white" strokeWidth="1.2" fill="none" />
    <path d="M11 5C11.5523 5 12 4.55228 12 4C12 3.44772 11.5523 3 11 3C10.4477 3 10 3.44772 10 4C10 4.55228 10.4477 5 11 5Z" stroke="white" strokeWidth="1.2" fill="none" />
    <path d="M8 8C9.65685 8 11 6.65685 11 5C11 3.34315 9.65685 2 8 2C6.34315 2 5 3.34315 5 5C5 6.65685 6.34315 8 8 8Z" stroke="white" strokeWidth="1.2" fill="none" />
    <path d="M3 9C3.55228 9 4 8.55228 4 8C4 7.44772 3.55228 7 3 7C2.44772 7 2 7.44772 2 8C2 8.55228 2.44772 9 3 9Z" stroke="white" strokeWidth="1.2" fill="none" />
    <path d="M13 9C13.5523 9 14 8.55228 14 8C14 7.44772 13.5523 7 13 7C12.4477 7 12 7.44772 12 8C12 8.55228 12.4477 9 13 9Z" stroke="white" strokeWidth="1.2" fill="none" />
  </svg>
);

const PROPERTY_CHIPS = [
  { label: 'Lake', key: 'wb', value: 'Lake' },
  { label: 'Beach', key: 'wb', value: 'Beach' },
  { label: 'Riverside', key: 'wb', value: 'Riverside' },
  { label: 'Pool-only', key: 'wb', value: 'Pool-only' },
  { label: 'Trek', key: 'tt', value: 'Trek' },
  { label: 'Lawn', key: 'tt', value: 'Lawn' },
  { label: 'Open Area', key: 'tt', value: 'Open Area' }
];

/**
 * Mobile full-screen filter (design match) + desktop centered sheet.
 * Same advanced filter state as desktop sidebar.
 */
const FilterModal = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onResetFilters,
  searchFilters,
  cityOptions = [],
  hideNearMe = false,
  resultCount = 0
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const primary = filters.primary || {};
  const secondary = filters.secondary || {};

  const updatePrimary = (key, v) => {
    onFiltersChange((prev) => ({
      ...prev,
      primary: { ...(prev.primary || {}), [key]: v }
    }));
  };

  const toggleWaterbody = (opt) => {
    const cur = primary.waterbody || [];
    const next = cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt];
    updatePrimary('waterbody', next);
  };

  const toggleTrekTrail = (opt) => {
    const cur = primary.trekTrail || [];
    const next = cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt];
    updatePrimary('trekTrail', next);
  };

  const chipSelected = (chip) => {
    if (chip.key === 'wb') return (primary.waterbody || []).includes(chip.value);
    return (primary.trekTrail || []).includes(chip.value);
  };

  const onChipClick = (chip) => {
    if (chip.key === 'wb') toggleWaterbody(chip.value);
    else toggleTrekTrail(chip.value);
  };

  const budgetValue = primary.budget || 0;
  const budgetPct = (budgetValue / 20) * 100;

  const handleReset = () => {
    const next = createDefaultAdvancedFilters();
    onResetFilters(next);
  };

  if (!isOpen) return null;

  const loc = searchFilters?.location || 'Location';
  const dateLine =
    searchFilters?.checkIn && searchFilters?.checkOut
      ? `${formatDate(searchFilters.checkIn)} - ${formatDate(searchFilters.checkOut)}`
      : 'Dates';
  const guestLine = (() => {
    if (searchFilters?.rooms == null || searchFilters?.adults == null) return 'Guests';
    const n = searchFilters.adults + (searchFilters.children || 0);
    return `${searchFilters.rooms} Room${searchFilters.rooms === 1 ? '' : 's'} ${n} guest`;
  })();

  const countLabel = resultCount === 1 ? '1 Property' : `${resultCount} Properties`;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 md:items-center md:justify-center md:bg-black/45 md:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-modal-title"
      onClick={onClose}
    >
      <div
        className="relative flex h-[100dvh] w-full max-h-[100dvh] flex-col overflow-hidden rounded-t-[24px] bg-[#FAF7F2] shadow-none animate-slide-up md:h-auto md:max-h-[min(92vh,820px)] md:max-w-[440px] md:animate-none md:rounded-[24px] md:shadow-2xl"
        style={{ backgroundColor: CREAM }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Coral header */}
        <header className="shrink-0 p-4" style={{ backgroundColor: CORAL }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full hover:bg-white/10"
                aria-label="Close filters"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M5 12H19" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
                  <path d="M5 12L11 18" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5 12L11 6" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <h2 id="filter-modal-title" className="text-[20px] font-regular leading-none tracking-tight text-white">
                Filter
              </h2>
              <FilterSectionBoneIcon size={20} className="shrink-0 text-white" />
            </div>
            <button
              type="button"
              onClick={handleReset}
              className="hidden text-[13px] font-medium text-white/90 underline decoration-white/60 md:inline"
            >
              Reset
            </button>
          </div>

          <div className="mt-4 border-b border-[#FF8274]" aria-hidden />

          <div className="p-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-[12px] font-medium leading-tight tracking-tight text-[#FFDEDA]">
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={14} strokeWidth={2} className="shrink-0 opacity-95" aria-hidden />
              <span className="max-w-[140px] truncate">{loc}</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={14} strokeWidth={2} className="shrink-0 opacity-95" aria-hidden />
              <span>{dateLine}</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <DoorOpen size={14} strokeWidth={2} className="shrink-0 opacity-95" aria-hidden />
              <span>{guestLine}</span>
            </span>
          </div>
        </header>

        {/* Scrollable cream body */}
        <div className="min-h-0 flex-1 overflow-y-auto no-scrollbar overscroll-contain px-4 py-[40px]">
          {/* Property type */}
          <section>
            <div className="mb-4 flex items-center gap-2">
              <h3 className="text-[18px] font-medium text-black">Property type</h3>
              <FilterSectionBoneIcon size={20} className="shrink-0 text-black" />
            </div>
            <div className="flex flex-wrap gap-[4px]">
              {PROPERTY_CHIPS.map((chip) => {
                const sel = chipSelected(chip);
                return (
                  <button
                    key={`${chip.key}-${chip.value}`}
                    type="button"
                    onClick={() => onChipClick(chip)}
                    className={`${filterChipBtn(sel)} p-[10px] text-[13px] font-semibold leading-tight`}
                  >
                    <ChipPaw selected={sel} />
                    <span>{chip.label}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <FilterDashedSeparator className="my-6" />

          {/* Budget */}
          <section>
            <div className="mb-1 flex items-center gap-2">
              <h3 className="text-[18px] font-medium text-black">
                Budget : ₹{budgetValue}K
              </h3>
              <FilterSectionBoneIcon size={20} className="shrink-0 text-black" />
            </div>
            <div className="relative mt-4 pb-2">
              <div className="mb-2 flex justify-between text-[13px] font-medium text-black">
                <span>₹0</span>
                <span>₹20K</span>
              </div>
              <div className="relative h-[10px] w-full bg-[#D8D8D8]">
                <div
                  className="absolute left-0 top-0 h-full  bg-black"
                  style={{ width: `${budgetPct}%` }}
                />
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={budgetValue}
                  onChange={(e) => updatePrimary('budget', parseInt(e.target.value, 10))}
                  className="absolute inset-0 z-10 h-[44px] w-full cursor-pointer opacity-0"
                  aria-label="Maximum budget per night in thousands"
                />
                <div
                  className="pointer-events-none absolute top-1/2 z-20 flex h-[34px] w-[34px] -translate-y-1/2 items-center justify-center rounded-full bg-black shadow-md"
                  style={{ left: `clamp(0px, calc(${budgetPct}% - 26px), calc(100% - 17px))` }}
                >
                  <SliderThumbPaw />
                </div>
              </div>
            </div>
          </section>

          <FilterDashedSeparator className="my-6" />

          {/* Pet charges */}
          <section>
            <div className="mb-4 flex items-center gap-2">
              <h3 className="text-[18px] font-medium text-black">Pet charges</h3>
              <FilterSectionBoneIcon size={20} className="shrink-0 text-black" />
            </div>
            <div className="flex flex-wrap gap-[4px]">
              {['Free', 'Paid'].map((option) => {
                const sel = primary.petCharges === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => updatePrimary('petCharges', sel ? null : option)}
                    className={`${filterChipBtn(sel)} p-[10px] text-[13px] font-semibold leading-tight`}
                  >
                    <ChipPaw selected={sel} />
                    <span>{option}</span>
                  </button>
                );
              })}
            </div>
            {primary.petCharges === 'Paid' && (
              <div className="mt-4">
                <p className="mb-2 text-[12px] text-[#6b7280]">Max pet charge (₹K)</p>
                <div className="relative h-[10px] w-full rounded-full bg-[#D8D8D8]">
                  <div
                    className="absolute left-0 top-0 h-full rounded-full bg-black"
                    style={{ width: `${((primary.petChargesAmount || 0) / 10) * 100}%` }}
                  />
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={primary.petChargesAmount || 0}
                    onChange={(e) => updatePrimary('petChargesAmount', parseInt(e.target.value, 10))}
                    className="absolute inset-0 z-10 h-10 w-full cursor-pointer opacity-0"
                  />
                  <div
                    className="pointer-events-none absolute top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black"
                    style={{ left: `clamp(0px, calc(${((primary.petChargesAmount || 0) / 10) * 100}% - 20px), calc(100% - 40px))` }}
                  >
                    <SliderThumbPaw />
                  </div>
                </div>
              </div>
            )}
          </section>

          <FilterDashedSeparator className="my-6" />

          {/* Dogs in pool */}
          <section>
            <div className="mb-4 flex items-center gap-2">
              <h3 className="text-[18px] font-medium text-black">Dogs in pool</h3>
              <FilterSectionBoneIcon size={20} className="shrink-0 text-black" />
            </div>
            <div className="flex flex-wrap gap-[4px]">
              {['Yes', 'No', 'Separate Pet Pool'].map((option) => {
                const sel = primary.dogsInPool === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => updatePrimary('dogsInPool', sel ? null : option)}
                    className={`${filterChipBtn(sel)} p-[10px] text-[13px] font-semibold leading-tight`}
                  >
                    <ChipPaw selected={sel} />
                    <span>{option}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <FilterDashedSeparator className="my-6" />

          {/* Ratings */}
          <section>
            <div className="mb-4 flex items-center gap-2">
              <h3 className="text-[18px] font-medium text-black">Ratings</h3>
              <FilterSectionBoneIcon size={20} className="shrink-0 text-black" />
            </div>
            <div className="flex flex-wrap gap-[4px]">
              {['4.0+', '4.5+', '4.8+'].map((option) => {
                const rating = parseFloat(option);
                const sel = primary.minRating === rating;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => updatePrimary('minRating', sel ? 0 : rating)}
                    className={`${filterChipBtn(sel)} p-[10px] text-[13px] font-semibold leading-tight`}
                  >
                    <ChipPaw selected={sel} />
                    <span>Min {option}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <FilterDashedSeparator className="my-6" />

          {/* City */}
          <section>
            <div className="mb-3 flex items-center gap-2">
              <h3 className="text-[18px] font-medium text-black">City / region</h3>
              <FilterSectionBoneIcon size={20} className="shrink-0 text-black" />
            </div>
            <select
              value={primary.filterCity || ''}
              onChange={(e) => updatePrimary('filterCity', e.target.value)}
              className="w-full rounded-[14px] border-2 border-[#e8e6e0] bg-white px-4 py-3.5 text-[15px] font-medium text-black"
            >
              <option value="">Any</option>
              {cityOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            {!hideNearMe && (
              <button
                type="button"
                className="mt-3 w-full rounded-[999px] border-2 border-black bg-black py-3 text-[14px] font-semibold text-white"
                onClick={() => {
                  if (!navigator.geolocation) {
                    alert('Location is not supported in this browser.');
                    return;
                  }
                  navigator.geolocation.getCurrentPosition(
                    (pos) => {
                      updatePrimary('nearMe', {
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                        radiusKm: primary.nearMe?.radiusKm ?? 50
                      });
                    },
                    () => alert('Could not get your location. Check permissions.')
                  );
                }}
              >
                Near me
              </button>
            )}
            {primary.nearMe && (
              <button type="button" className="mt-2 text-[13px] font-medium text-[#666] underline" onClick={() => updatePrimary('nearMe', null)}>
                Clear near me
              </button>
            )}
          </section>

          <FilterDashedSeparator className="my-6" />

          <div className="pb-4">
            <ListingAttributesFilterForm
              secondary={secondary}
              onChange={(next) => onFiltersChange((prev) => ({ ...prev, secondary: next }))}
              headingBoneClassName="shrink-0 text-black"
              useModalChipStyle
            />
          </div>
        </div>

        {/* Footer CTA */}
        <div
          className="shrink-0 border-t border-[#e8e6df] px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3"
          style={{ backgroundColor: CREAM }}
        >
          <button
            type="button"
            onClick={onClose}
            className="relative flex h-[56px] w-full items-center justify-center overflow-hidden rounded-[999px] bg-black text-[16px] font-bold text-white"
          >
            <span>View {countLabel}</span>
            <svg className='block md:hidden absolute right-[10%] bottom-[-5%]' xmlns="http://www.w3.org/2000/svg" width="61" height="36" viewBox="0 0 61 36" fill="none">
              <path d="M17.8291 8.93766C17.4553 8.93766 17.0741 8.84601 16.7368 8.76537C15.8388 8.5491 13.7384 8.04692 14.2406 5.94657C14.4348 5.13283 14.8784 4.42354 15.4887 3.94885C16.1375 3.44301 16.9274 3.25424 17.6623 3.43019C19.1322 3.78208 19.99 5.48471 19.5739 7.22401C19.244 8.60224 18.5476 8.93766 17.8291 8.93766ZM17.1107 4.83226C16.7955 4.83226 16.5315 4.99538 16.3886 5.10717C16.0458 5.37476 15.7819 5.80545 15.6665 6.2893C15.5602 6.731 15.5748 6.98025 17.0777 7.33947C17.3123 7.39629 17.8053 7.51176 17.9171 7.46594C17.9171 7.46594 18.0289 7.37613 18.1462 6.88311C18.3698 5.94657 17.9923 5.0192 17.3196 4.85792C17.2482 4.84143 17.1785 4.83226 17.1107 4.83226Z" fill="#808080" />
              <path d="M9.68653 8.10003C9.35114 8.10003 9.01389 8.05238 8.70599 7.90026C8.1305 7.61435 7.79693 7.04986 7.68879 6.17929C7.58982 5.38204 7.74927 4.59395 8.13781 3.95982C8.55385 3.28352 9.20448 2.84365 9.92659 2.75568C10.6432 2.66587 11.3855 2.93164 11.9555 3.48514C12.4888 4.00381 12.8352 4.72956 12.9342 5.52682C13.0716 6.62648 12.5694 7.21665 12.1222 7.51722C11.6182 7.85811 10.9695 7.97539 10.3995 8.04687C10.1704 8.07619 9.92846 8.10003 9.68287 8.10003H9.68653ZM10.2088 4.20173C10.1759 4.20173 10.1429 4.20357 10.108 4.20907C9.72865 4.25672 9.49224 4.55362 9.38777 4.7259C9.17517 5.07229 9.08718 5.53416 9.144 5.99602C9.18432 6.31858 9.26499 6.53851 9.35846 6.58616C9.53807 6.67597 10.0347 6.61366 10.2217 6.58983C10.5956 6.54401 11.0556 6.46888 11.303 6.30026C11.3891 6.24161 11.5358 6.14264 11.4808 5.70461C11.424 5.24275 11.2242 4.81388 10.9328 4.53164C10.8008 4.40334 10.5406 4.1999 10.2088 4.1999V4.20173Z" fill="#808080" />
              <path d="M4.47307 12.2714C3.619 12.2714 3.12602 11.7308 2.90609 11.4045C2.08684 10.1839 2.35989 8.55825 3.51637 7.78115C4.67284 7.00589 6.28202 7.36512 7.1031 8.58574C7.52281 9.21071 7.6291 9.81002 7.422 10.369C7.21123 10.939 6.72188 11.3221 6.31867 11.5933C5.57274 12.0937 4.96425 12.2714 4.47307 12.2714ZM4.87631 8.83499C4.6857 8.83499 4.49689 8.88815 4.33377 8.99628C3.84809 9.32251 3.75281 10.0355 4.12302 10.5853C4.22199 10.7337 4.45659 11.0765 5.50127 10.3745C5.91181 10.0996 6.01994 9.93281 6.04743 9.85767C6.08958 9.74221 5.9833 9.5461 5.88617 9.40131C5.64058 9.03659 5.25203 8.83316 4.87631 8.83316V8.83499Z" fill="#808080" />
              <path d="M22.6033 13.0027C21.9069 13.0027 21.2874 12.5867 20.8989 12.3264C20.2739 11.9049 18.3678 10.6256 19.6837 8.66823C20.7192 7.12687 22.6198 6.58254 23.9193 7.45677C25.2187 8.331 25.435 10.2939 24.3995 11.8353C23.8056 12.7205 23.1788 13.0027 22.6033 13.0027ZM22.5117 8.50146C21.9545 8.50146 21.3149 8.86618 20.9007 9.48382C20.591 9.94568 20.492 10.2847 21.7163 11.1077C22.6235 11.7161 22.7463 11.6666 23.1825 11.016C23.758 10.1601 23.7195 9.0861 23.1018 8.6719C22.9296 8.55643 22.7261 8.50146 22.5117 8.50146Z" fill="#808080" />
              <path d="M22.7727 42.5084H4.44509C4.24348 42.5084 4.05106 42.4259 3.91177 42.2793C3.77431 42.1327 3.70098 41.9348 3.71198 41.735C3.71931 41.603 4.44325 28.4933 4.44325 22.7146C4.44325 18.8547 3.80547 16.3274 3.36744 15.0444C2.50604 14.5423 1.74727 13.822 1.16079 12.9496C-0.642656 10.2646 -0.307292 6.8703 1.90669 5.3821C2.92021 4.70214 4.20499 4.51885 5.48976 4.84325C5.75001 2.29754 7.38668 0.307158 9.58417 0.0322431C11.2776 -0.180358 12.9088 0.670053 13.9828 2.2389C15.1979 1.03661 16.7686 0.521594 18.2715 0.882649C20.0181 1.30235 21.3157 2.84371 21.6914 4.85059C22.9909 4.5042 24.2701 4.68748 25.3075 5.38393C27.5215 6.87214 27.8568 10.2664 26.0534 12.9514C25.4669 13.8256 24.7082 14.5441 23.8468 15.0463C23.4069 16.3274 22.7691 18.8529 22.7691 22.7164C22.7691 28.4933 23.493 41.6049 23.5003 41.7368C23.5113 41.9384 23.4398 42.1345 23.3006 42.2812C23.1613 42.4278 22.9688 42.5103 22.7672 42.5103L22.7727 42.5084ZM5.21851 41.0422H21.9975C21.8417 38.1244 21.3047 27.7125 21.3047 22.7146C21.3047 18.3507 22.0928 15.5594 22.562 14.2838C22.6243 14.1134 22.7489 13.9723 22.912 13.888C23.6635 13.4958 24.3288 12.8891 24.8383 12.1322C26.1909 10.1198 26.035 7.6364 24.4919 6.60089C23.6415 6.0309 22.5052 5.99973 21.3725 6.5184C21.1508 6.62104 20.8923 6.60455 20.6816 6.47625C20.4708 6.34796 20.3425 6.12253 20.3333 5.87877C20.2673 4.07166 19.3015 2.63661 17.9288 2.30855C16.7063 2.0153 15.3757 2.63845 14.4611 3.93788C14.3108 4.15048 14.0616 4.2696 13.794 4.24577C13.5319 4.22194 13.3047 4.05884 13.1947 3.82058C12.4671 2.23524 11.1237 1.32069 9.76378 1.48747C8.0648 1.70007 6.81669 3.60981 6.92482 5.83845C6.93765 6.09504 6.81302 6.3388 6.60042 6.48359C6.38599 6.62655 6.11109 6.64669 5.88016 6.53673C4.73651 5.99973 3.58737 6.02174 2.72231 6.60089C1.17912 7.63824 1.02331 10.1198 2.37589 12.1322C2.8854 12.8891 3.55069 13.4976 4.30213 13.888C4.46341 13.9723 4.58802 14.1134 4.65217 14.2857C5.12136 15.5613 5.90947 18.3544 5.90947 22.7146C5.90947 27.7125 5.37246 38.1244 5.21667 41.0422H5.21851Z" fill="#808080" />
              <path d="M16.1762 19.7822C15.3551 19.7822 14.9995 19.5824 14.6495 19.3405C14.4314 19.1902 14.2261 19.0491 13.2438 19.0491C12.2614 19.0491 12.0561 19.1902 11.838 19.3405C11.488 19.5824 11.1324 19.7822 10.3113 19.7822C9.29598 19.7822 7.37891 18.8621 7.37891 15.3835C7.37891 12.134 10.5881 9.51868 13.2438 9.51868C15.8994 9.51868 19.1086 12.134 19.1086 15.3835C19.1086 18.8621 17.1915 19.7822 16.1762 19.7822ZM13.2438 17.5828C14.5743 17.5828 15.0454 17.8321 15.4816 18.1345C15.6612 18.2591 15.7436 18.316 16.1762 18.316C16.5849 18.316 17.6424 17.7881 17.6424 15.3835C17.6424 12.8323 15.0142 10.9849 13.2438 10.9849C11.4733 10.9849 8.84512 12.8323 8.84512 15.3835C8.84512 17.7881 9.90262 18.316 10.3113 18.316C10.742 18.316 10.8245 18.2591 11.006 18.1345C11.444 17.8339 11.9132 17.5828 13.2438 17.5828Z" fill="#808080" />
              <path d="M42.3818 8.93766C42.7557 8.93766 43.1369 8.84601 43.4741 8.76537C44.3722 8.5491 46.4725 8.04692 45.9704 5.94657C45.7761 5.13283 45.3325 4.42354 44.7222 3.94885C44.0734 3.44301 43.2835 3.25424 42.5486 3.43019C41.0787 3.78208 40.221 5.48471 40.637 7.22401C40.9669 8.60224 41.6633 8.93766 42.3818 8.93766ZM43.1002 4.83226C43.4155 4.83226 43.6794 4.99538 43.8224 5.10717C44.1651 5.37476 44.429 5.80545 44.5445 6.2893C44.6508 6.731 44.6361 6.98025 43.1332 7.33947C42.8986 7.39629 42.4056 7.51176 42.2938 7.46594C42.2938 7.46594 42.182 7.37613 42.0647 6.88311C41.8411 5.94657 42.2187 5.0192 42.8913 4.85792C42.9628 4.84143 43.0324 4.83226 43.1002 4.83226Z" fill="#808080" />
              <path d="M50.5283 8.10003C50.8637 8.10003 51.201 8.05238 51.5089 7.90026C52.0843 7.61435 52.4179 7.04986 52.526 6.17929C52.625 5.38204 52.4656 4.59395 52.077 3.95982C51.661 3.28352 51.0104 2.84365 50.2883 2.75568C49.5716 2.66587 48.8294 2.93164 48.2594 3.48514C47.726 4.00381 47.3796 4.72956 47.2807 5.52682C47.1432 6.62648 47.6454 7.21665 48.0926 7.51722C48.5966 7.85811 49.2454 7.97539 49.8154 8.04687C50.0445 8.07619 50.2864 8.10003 50.532 8.10003H50.5283ZM50.006 4.20173C50.039 4.20173 50.072 4.20357 50.1068 4.20907C50.4862 4.25672 50.7226 4.55362 50.8271 4.7259C51.0397 5.07229 51.1277 5.53416 51.0708 5.99602C51.0305 6.31858 50.9499 6.53851 50.8564 6.58616C50.6768 6.67597 50.1801 6.61366 49.9932 6.58983C49.6193 6.54401 49.1593 6.46888 48.9118 6.30026C48.8257 6.24161 48.6791 6.14264 48.7341 5.70461C48.7909 5.24275 48.9907 4.81388 49.2821 4.53164C49.414 4.40334 49.6743 4.1999 50.006 4.1999V4.20173Z" fill="#808080" />
              <path d="M55.7418 12.2714C56.5958 12.2714 57.0888 11.7308 57.3088 11.4045C58.128 10.1839 57.855 8.55825 56.6985 7.78115C55.542 7.00589 53.9328 7.36512 53.1117 8.58574C52.692 9.21071 52.5857 9.81002 52.7928 10.369C53.0036 10.939 53.493 11.3221 53.8962 11.5933C54.6421 12.0937 55.2506 12.2714 55.7418 12.2714ZM55.3385 8.83499C55.5291 8.83499 55.718 8.88815 55.8811 8.99628C56.3668 9.32251 56.462 10.0355 56.0918 10.5853C55.9929 10.7337 55.7583 11.0765 54.7136 10.3745C54.303 10.0996 54.1949 9.93281 54.1674 9.85767C54.1253 9.74221 54.2315 9.5461 54.3287 9.40131C54.5743 9.03659 54.9628 8.83316 55.3385 8.83316V8.83499Z" fill="#808080" />
              <path d="M37.6115 13.0027C38.308 13.0027 38.9274 12.5867 39.316 12.3264C39.941 11.9049 41.8471 10.6256 40.5311 8.66823C39.4956 7.12687 37.595 6.58254 36.2956 7.45677C34.9962 8.331 34.7799 10.2939 35.8154 11.8353C36.4092 12.7205 37.036 13.0027 37.6115 13.0027ZM37.7031 8.50146C38.2603 8.50146 38.8999 8.86618 39.3142 9.48382C39.6239 9.94568 39.7229 10.2847 38.4986 11.1077C37.5914 11.7161 37.4686 11.6666 37.0324 11.016C36.4569 10.1601 36.4954 9.0861 37.113 8.6719C37.2853 8.55643 37.4887 8.50146 37.7031 8.50146Z" fill="#808080" />
              <path d="M37.4421 42.5084H55.7698C55.9714 42.5084 56.1638 42.4259 56.3031 42.2793C56.4405 42.1327 56.5139 41.9348 56.5029 41.735C56.4955 41.603 55.7716 28.4933 55.7716 22.7146C55.7716 18.8547 56.4094 16.3274 56.8474 15.0444C57.7088 14.5423 58.4676 13.822 59.0541 12.9496C60.8575 10.2646 60.5221 6.8703 58.3082 5.3821C57.2946 4.70214 56.0099 4.51885 54.7251 4.84325C54.4648 2.29754 52.8282 0.307158 50.6307 0.0322431C48.9372 -0.180358 47.306 0.670053 46.232 2.2389C45.0169 1.03661 43.4463 0.521594 41.9434 0.882649C40.1968 1.30235 38.8991 2.84371 38.5234 4.85059C37.224 4.5042 35.9447 4.68748 34.9074 5.38393C32.6934 6.87214 32.358 10.2664 34.1615 12.9514C34.748 13.8256 35.5067 14.5441 36.3681 15.0463C36.8079 16.3274 37.4458 18.8529 37.4458 22.7164C37.4458 28.4933 36.7218 41.6049 36.7145 41.7368C36.7035 41.9384 36.775 42.1345 36.9143 42.2812C37.0536 42.4278 37.246 42.5103 37.4476 42.5103L37.4421 42.5084ZM54.9963 41.0422H38.2174C38.3731 38.1244 38.9101 27.7125 38.9101 22.7146C38.9101 18.3507 38.122 15.5594 37.6529 14.2838C37.5905 14.1134 37.4659 13.9723 37.3028 13.888C36.5514 13.4958 35.8861 12.8891 35.3766 12.1322C34.024 10.1198 34.1798 7.6364 35.723 6.60089C36.5734 6.0309 37.7097 5.99973 38.8423 6.5184C39.0641 6.62104 39.3225 6.60455 39.5333 6.47625C39.744 6.34796 39.8724 6.12253 39.8815 5.87877C39.9475 4.07166 40.9133 2.63661 42.2861 2.30855C43.5085 2.0153 44.8392 2.63845 45.7537 3.93788C45.904 4.15048 46.1532 4.2696 46.4208 4.24577C46.6829 4.22194 46.9102 4.05884 47.0201 3.82058C47.7477 2.23524 49.0912 1.32069 50.4511 1.48747C52.15 1.70007 53.3982 3.60981 53.29 5.83845C53.2772 6.09504 53.4018 6.3388 53.6144 6.48359C53.8289 6.62655 54.1038 6.64669 54.3347 6.53673C55.4783 5.99973 56.6275 6.02174 57.4925 6.60089C59.0357 7.63824 59.1915 10.1198 57.839 12.1322C57.3294 12.8891 56.6642 13.4976 55.9127 13.888C55.7514 13.9723 55.6268 14.1134 55.5627 14.2857C55.0935 15.5613 54.3054 18.3544 54.3054 22.7146C54.3054 27.7125 54.8424 38.1244 54.9982 41.0422H54.9963Z" fill="#808080" />
              <path d="M44.0387 19.7822C44.8597 19.7822 45.2153 19.5824 45.5654 19.3405C45.7835 19.1902 45.9887 19.0491 46.9711 19.0491C47.9534 19.0491 48.1587 19.1902 48.3768 19.3405C48.7269 19.5824 49.0824 19.7822 49.9035 19.7822C50.9189 19.7822 52.8359 18.8621 52.8359 15.3835C52.8359 12.134 49.6268 9.51868 46.9711 9.51868C44.3154 9.51868 41.1062 12.134 41.1062 15.3835C41.1062 18.8621 43.0233 19.7822 44.0387 19.7822ZM46.9711 17.5828C45.6405 17.5828 45.1695 17.8321 44.7333 18.1345C44.5537 18.2591 44.4712 18.316 44.0387 18.316C43.63 18.316 42.5724 17.7881 42.5724 15.3835C42.5724 12.8323 45.2006 10.9849 46.9711 10.9849C48.7415 10.9849 51.3697 12.8323 51.3697 15.3835C51.3697 17.7881 50.3122 18.316 49.9035 18.316C49.4728 18.316 49.3903 18.2591 49.2089 18.1345C48.7709 17.8339 48.3017 17.5828 46.9711 17.5828Z" fill="#808080" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;
